import type { OfflineHandoverArgs } from '@/tauri/online-chunk-upload/session';
import { GenerationMethod, TranscribeMode } from '@heidi/schema/ml-scribe';
import type { QuerySessionResponse } from '@heidi/session/contracts';
import { DEFAULT_TEMPLATE_AUTO_MODE } from '@heidi/session/contracts';

/**
 * The template the note would be generated with if the clinician stopped
 * online now, resolved the way `_actionPostRecording` resolves it, or
 * `undefined` when that path would open the template picker instead.
 *
 * Resolved here and sent explicitly rather than left to the sync: on a reused
 * consult Rust keeps a stored template when the stop sends none, and a session
 * on Auto can still carry an old `template_id`, so leaving the choice to
 * whatever the row or the session happens to hold picks the wrong template.
 *
 * 1. Nothing when the account always asks (`alwaysAsks`, the Shadow Heidi
 *    trial): online that prompts before any template is considered.
 * 2. A specific template chosen on the session (not Auto) wins.
 * 3. Otherwise a specific account default wins, Auto on the session included:
 *    online, Auto is not treated as an explicit choice.
 * 4. Otherwise Auto, when the session or the account default says so, or
 *    when online would generate Auto without asking (`generatesAuto`: the
 *    note-entry experiment's `auto_generate` arm). Never on
 *    an EHR-linked session, where Auto must not be chosen silently, and never
 *    when online would show a picker in Auto's place (`asksInsteadOfAuto`: the
 *    first-note template selection, or a picker arm of the note-entry
 *    experiment).
 * 5. Otherwise nothing: the picker decides.
 */
export function resolveHandoverTemplateId(
  session: QuerySessionResponse | undefined,
  defaultTemplateId: string | null | undefined,
  { alwaysAsks = false, asksInsteadOfAuto = false, generatesAuto = false } = {}
): string | undefined {
  if (alwaysAsks) {
    return undefined;
  }
  const note = session?.consult_note;
  const sessionIsAuto = note?.generation_method === GenerationMethod.AUTO_MODE;
  // Online counts a template as chosen only with a generation method beside it.
  if (note?.generation_method && !sessionIsAuto && note.template_id) {
    return note.template_id;
  }
  if (defaultTemplateId && defaultTemplateId !== DEFAULT_TEMPLATE_AUTO_MODE) {
    return defaultTemplateId;
  }
  const wouldAuto =
    sessionIsAuto || defaultTemplateId === DEFAULT_TEMPLATE_AUTO_MODE || generatesAuto;
  if (wouldAuto && !session?.ehr_provider && !asksInsteadOfAuto) {
    return DEFAULT_TEMPLATE_AUTO_MODE;
  }
  return undefined;
}

/**
 * Whether the session's note is an AI command rather than a template. Online
 * that is an explicit choice streamed as the command; the queued consult has no
 * column for a command, so such a stop must not be handed over.
 */
export function sessionChoseAiCommand(session: QuerySessionResponse): boolean {
  const note = session.consult_note;
  return (
    !!note?.generation_method &&
    note.generation_method !== GenerationMethod.AUTO_MODE &&
    !!(note.ai_command_id || note.ai_command_text)
  );
}

/**
 * The settings a stop sends so Rust can queue the audio it could not upload on
 * an offline consult bound to this server session. The reconnect sync writes
 * them back onto the server session and generates the note with them.
 *
 * `pendingTranscribeMode` wins over the cached mode: it is a toggle made while
 * offline that has not reached the server yet, and the sync writes
 * `dictationType` back onto the session, so the stale value would undo it.
 */
export function buildOfflineHandoverArgs({
  session,
  pendingTranscribeMode,
  defaultTemplateId,
  pickerRouting,
}: {
  session: QuerySessionResponse;
  pendingTranscribeMode: TranscribeMode | undefined;
  defaultTemplateId: string | null | undefined;
  pickerRouting?: {
    alwaysAsks?: boolean;
    asksInsteadOfAuto?: boolean;
    generatesAuto?: boolean;
  };
}): OfflineHandoverArgs {
  const dictationType = pendingTranscribeMode ?? session.transcribe_mode ?? undefined;
  return {
    // Dictate generates no templated note, so it carries no template.
    templateId:
      dictationType === TranscribeMode.DICTATE
        ? undefined
        : resolveHandoverTemplateId(session, defaultTemplateId, pickerRouting),
    dictationType,
    language: session.language_code ?? undefined,
    outputLanguage: session.output_language_code ?? undefined,
    noteStyle: session.consult_note?.voice_style ?? undefined,
  };
}

/**
 * Whether a handed-over stop should open the template picker, so the clinician
 * chooses before the sync generates the note. Dictate never asks.
 */
export function handoverNeedsTemplatePicker(args: OfflineHandoverArgs): boolean {
  return args.dictationType !== TranscribeMode.DICTATE && !args.templateId;
}
